import {
  ECodeBlockType,
  EDiffStatus,
  type ICodeBlock,
  type IBlockDiff,
  type ISection,
} from './diff-types';

// =============================================================================
// Constants
// =============================================================================

/** Regex patterns for parsing markdown */
const HEADER_PATTERN = /^(#{1,6})\s+([^\n]+)$/;
const LIST_PATTERN = /^(\s*[-*+]|\s*\d+\.)\s+([^\n]+)$/;
const ORDERED_LIST_MARKER_PATTERN = /^\s*\d+\.$/;

/** Regex to match HTML comments (e.g., <!-- @vars: [...] -->) */
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;

// =============================================================================
// Block Diff Helpers
// =============================================================================

/**
 * Creates a block diff object with common properties.
 *
 * This is a factory function that constructs IBlockDiff objects with the
 * appropriate content key (oldContent or newContent) based on whether
 * the block is being added or removed.
 *
 * @param status - The diff status (ADDED, REMOVED, MODIFIED, UNCHANGED)
 * @param block - The source code block containing type, level, and content
 * @param contentKey - Which content property to populate ('oldContent' for removals, 'newContent' for additions)
 * @returns A block diff object ready for rendering
 */
function createBlockDiff(
  status: EDiffStatus,
  block: ICodeBlock,
  contentKey: 'oldContent' | 'newContent',
): IBlockDiff {
  return {
    status,
    blockType: block.type,
    level: block.level,
    [contentKey]: block.content,
  };
}

/**
 * Creates a block diff representing a removed block.
 *
 * Used when a block exists in the old document but not in the new one.
 * The block's content is stored in oldContent for rendering with deletion marks.
 *
 * @param block - The block that was removed from the document
 * @returns A block diff with REMOVED status and oldContent populated
 */
function createRemovedDiff(block: ICodeBlock): IBlockDiff {
  return createBlockDiff(EDiffStatus.REMOVED, block, 'oldContent');
}

/**
 * Creates a block diff representing an added block.
 *
 * Used when a block exists in the new document but not in the old one.
 * The block's content is stored in newContent for rendering with addition marks.
 *
 * @param block - The block that was added to the document
 * @returns A block diff with ADDED status and newContent populated
 */
function createAddedDiff(block: ICodeBlock): IBlockDiff {
  return createBlockDiff(EDiffStatus.ADDED, block, 'newContent');
}

// =============================================================================
// Parsing Functions
// =============================================================================

/**
 * Parses AOP markdown code into typed blocks.
 *
 * @param markdown - The markdown string to parse
 * @returns Array of parsed code blocks
 *
 * @example
 * ```
 * # Title
 * ## Required Information
 * - Item 1
 * - Item 2
 * ```
 * @returns Array of parsed code blocks [
 *   { type: 'heading', level: 1, content: 'Title' },
 *   { type: 'heading', level: 2, content: 'Required Information' },
 *   { type: 'orderedList', content: 'Item 1\nItem 2' },
 * ]
 */
export function parseAopCodeToBlocks(markdown: string): ICodeBlock[] {
  // Strip HTML comments (e.g., <!-- @vars: [...] -->) before parsing, and clean up any trialing whitespace
  // Replace comments with space (becomes empty after trimEnd, preserving line structure)
  const cleanedMarkdown = markdown
    .replace(HTML_COMMENT_PATTERN, ' ')
    .split('\n')
    .map(line => line.trimEnd())
    .join('\n');
  const lines = cleanedMarkdown.split('\n');
  const blocks: ICodeBlock[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;

    const content = paragraphLines.join('\n').trim();
    if (content) {
      blocks.push({ type: ECodeBlockType.PARAGRAPH, content });
    }
    paragraphLines = [];
  };

  for (const line of lines) {
    // Check for header
    const headerMatch = line.match(HEADER_PATTERN);
    if (headerMatch) {
      flushParagraph();
      blocks.push({
        type: ECodeBlockType.HEADING,
        level: headerMatch[1].length,
        content: headerMatch[2],
      });
      continue;
    }

    // Check for list item (ordered or unordered)
    const listMatch = line.match(LIST_PATTERN);
    if (listMatch) {
      flushParagraph();
      const marker = listMatch[1];
      const isOrdered = ORDERED_LIST_MARKER_PATTERN.test(marker);
      blocks.push({
        type: isOrdered
          ? ECodeBlockType.ORDERED_LIST
          : ECodeBlockType.UNORDERED_LIST,
        content: listMatch[2],
      });
      continue;
    }

    // Empty line flushes paragraph
    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    // Accumulate paragraph lines
    paragraphLines.push(line);
  }

  flushParagraph();
  return blocks;
}

/**
 * Groups blocks into sections based on headers.
 *
 * @param blocks - Array of code blocks to group
 * @returns Array of sections
 *
 * @example
 * ```
 * # Title
 * ## Required Information
 * - Item 1
 * - Item 2
 * ```
 * @returns Array of sections [
 *   { header: { type: 'heading', level: 1, content: 'Title' }, content: [
 *     { type: 'heading', level: 2, content: 'Required Information' },
 *     { type: 'orderedList', content: 'Item 1\nItem 2' },
 *   ] },
 * ]
 */
function groupBlocksIntoSections(blocks: ICodeBlock[]): ISection[] {
  const sections: ISection[] = [];
  let current: ISection = { header: null, content: [] };

  for (const block of blocks) {
    if (block.type === ECodeBlockType.HEADING) {
      if (current.header || current.content.length > 0) {
        sections.push(current);
      }
      current = { header: block, content: [] };
    } else {
      current.content.push(block);
    }
  }

  if (current.header || current.content.length > 0) {
    sections.push(current);
  }

  return sections;
}

// =============================================================================
// Section Diff Functions
// =============================================================================

/**
 * Diffs content blocks within a section.
 *
 * Compares blocks at the same index position. When blocks at the same position
 * have different types, both are emitted (old as removed, new as added).
 * When types match, content is compared to determine if modified or unchanged.
 *
 * Note: This positional approach means inserting a block at the beginning
 * will cause all subsequent blocks to appear as modified.
 *
 * @param oldContent - Content blocks from old section (non-header blocks)
 * @param newContent - Content blocks from new section (non-header blocks)
 * @returns Array of block diffs representing changes between the two content arrays
 *
 * @example
 * // Same content unchanged
 * diffSectionContent(
 *   [{ type: 'paragraph', content: 'Hello' }],
 *   [{ type: 'paragraph', content: 'Hello' }]
 * )
 * // Returns: [{ status: 'unchanged', blockType: 'paragraph', ... }]
 *
 * @example
 * // Content modified
 * diffSectionContent(
 *   [{ type: 'paragraph', content: 'Hello' }],
 *   [{ type: 'paragraph', content: 'Hello World' }]
 * )
 * // Returns: [{ status: 'modified', oldContent: 'Hello', newContent: 'Hello World', ... }]
 */
function diffSectionContent(
  oldContent: ICodeBlock[],
  newContent: ICodeBlock[],
): IBlockDiff[] {
  const result: IBlockDiff[] = [];
  const maxLen = Math.max(oldContent.length, newContent.length);

  // Note: Adding something to the beginning will mark subsequent blocks as modified.
  for (let i = 0; i < maxLen; i++) {
    const oldBlock = oldContent[i];
    const newBlock = newContent[i];

    if (oldBlock && newBlock) {
      const sameType =
        oldBlock.type === newBlock.type && oldBlock.level === newBlock.level;

      if (sameType) {
        result.push({
          status:
            oldBlock.content === newBlock.content
              ? EDiffStatus.UNCHANGED
              : EDiffStatus.MODIFIED,
          blockType: oldBlock.type,
          level: oldBlock.level,
          oldContent: oldBlock.content,
          newContent: newBlock.content,
        });
      } else {
        result.push(createRemovedDiff(oldBlock), createAddedDiff(newBlock));
      }
    } else if (oldBlock) {
      result.push(createRemovedDiff(oldBlock));
    } else if (newBlock) {
      result.push(createAddedDiff(newBlock));
    }
  }

  return result;
}

/**
 * Diffs a header block between two sections with special handling for structural headers.
 *
 * AOP documents have a consistent structure where level 2+ headers (## Required Information,
 * ## Process, etc.) should always appear unchanged. Only level 1 headers (# Title) show actual diff highlighting.
 *
 * @param oldHeader - The header block from the old section (null if section had no header)
 * @param newHeader - The header block from the new section (null if section had no header)
 * @returns A block diff for the header, or null if both headers are null
 *
 * @example
 * // Level 1 header modified - shows diff
 * diffHeader(
 *   { type: 'heading', level: 1, content: 'Old Title' },
 *   { type: 'heading', level: 1, content: 'New Title' }
 * )
 * // Returns: { status: 'modified', oldContent: 'Old Title', newContent: 'New Title', ... }
 *
 */
function diffHeader(
  oldHeader: ICodeBlock | null,
  newHeader: ICodeBlock | null,
): IBlockDiff | null {
  if (!oldHeader && !newHeader) return null;

  if (!oldHeader) {
    return createAddedDiff(newHeader!);
  }

  if (!newHeader) {
    return createRemovedDiff(oldHeader);
  }

  // Level 2+ headers are always unchanged (consistent structure)
  const STRUCTURAL_HEADING_LEVEL = 2;
  const isStructuralHeading = (level?: number) =>
    (level ?? 1) >= STRUCTURAL_HEADING_LEVEL;
  const isUnchanged =
    isStructuralHeading(oldHeader.level) ||
    oldHeader.content === newHeader.content;

  return {
    status: isUnchanged ? EDiffStatus.UNCHANGED : EDiffStatus.MODIFIED,
    blockType: ECodeBlockType.HEADING,
    level: oldHeader.level,
    oldContent: isStructuralHeading(oldHeader.level)
      ? newHeader.content
      : oldHeader.content,
    newContent: newHeader.content,
  };
}

/**
 * Computes diff between two AOP markdown documents.
 *
 * Since AOP documents have consistent structure:
 * - Level 2+ headers (## Required Information, etc.) are always unchanged
 * - Level 1 header (# Title) shows character-level diff
 * - Content within sections is compared with character-level diff
 *
 * @param oldCode - The old version of the markdown code
 * @param newCode - The new version of the markdown code
 * @returns Array of block diffs
 */
export function computeStructuredDiff(
  oldCode: string,
  newCode: string,
): IBlockDiff[] {
  const oldSections = groupBlocksIntoSections(parseAopCodeToBlocks(oldCode));
  const newSections = groupBlocksIntoSections(parseAopCodeToBlocks(newCode));
  const result: IBlockDiff[] = [];
  const maxSections = Math.max(oldSections.length, newSections.length);

  for (let i = 0; i < maxSections; i++) {
    const oldSection = oldSections[i];
    const newSection = newSections[i];

    if (oldSection && newSection) {
      // Both sections exist - diff header and content
      const headerDiff = diffHeader(oldSection.header, newSection.header);
      if (headerDiff) result.push(headerDiff);
      result.push(
        ...diffSectionContent(oldSection.content, newSection.content),
      );
    } else if (oldSection) {
      // Section removed
      if (oldSection.header) {
        result.push(createRemovedDiff(oldSection.header));
      }
      result.push(...oldSection.content.map(createRemovedDiff));
    } else if (newSection) {
      // Section added
      if (newSection.header) {
        result.push(createAddedDiff(newSection.header));
      }
      result.push(...newSection.content.map(createAddedDiff));
    }
  }

  return result;
}
